import { storeToRefs } from 'pinia'
import { computed, Ref } from 'vue'

import { ChatUserAvatar } from '@shared/models/interfaces/chat-user-avatar'
import { useVideoCallConfigsStore } from '@shared/store/video-call-configs'
import { defaultParticipantNameMap } from '@shared/utils/constants/video-call-configs'
import { getShortInitials } from '@shared/utils/helpers/chat/get-user-initials'
import { handleUserFullName } from '@shared/utils/helpers/user-name'
import { getParticipantRoleFromType } from '@shared/utils/helpers/video-call'

import { Participant } from '@protos/chat/participant_pb'
import { UserType } from '@protos/user_type_pb'

import { PlainMessage } from '@bufbuild/protobuf'

export const useVideoCallParticipants = (
  participants: Ref<Array<PlainMessage<Participant>>>
) => {
  const videoCallConfigsStore = useVideoCallConfigsStore()

  const { participantIdentities } = storeToRefs(videoCallConfigsStore)

  const participantOneId = computed(
    () => participantIdentities.value.first?.id ?? null
  )
  const participantTwoId = computed(
    () => participantIdentities.value.second?.id ?? null
  )
  const participantsLabels = computed(() => {
    const { first, second } = participantIdentities.value

    const participantOne = participants.value.find(
      (item) => item.chatUserId.toString() === first?.id
    )

    const participantTwo = participants.value.find(
      (item) => item.chatUserId.toString() === second?.id
    )

    const participantRoleOne = getParticipantRoleFromType(
      participantOne?.type ?? UserType.VIDEO_CALL_GUEST
    )

    const participantRoleTwo = getParticipantRoleFromType(
      participantTwo?.type ?? UserType.VIDEO_CALL_GUEST
    )

    const participantOneName =
      participantOne?.upDisplayName ||
      handleUserFullName(
        defaultParticipantNameMap[participantRoleOne],
        participantOne?.firstName,
        participantOne?.lastName
      )

    const participantTwoName =
      participantTwo?.upDisplayName ||
      handleUserFullName(
        defaultParticipantNameMap[participantRoleTwo],
        participantTwo?.firstName,
        participantTwo?.lastName
      )

    return {
      first: {
        avatar: {
          src: participantOne?.avatar?.url,
          initials: getShortInitials(participantOneName),
        } as ChatUserAvatar,
        name: participantOneName,
      },
      second: {
        avatar: {
          src: participantTwo?.avatar?.url,
          initials: getShortInitials(participantTwoName),
        } as ChatUserAvatar,
        name: participantTwoName,
      },
    }
  })

  return {
    participantOneId,
    participantTwoId,
    participantsLabels,
  }
}
